import React, { useEffect } from 'react';
import { Col, Form, Input, Row } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Button from 'components/Button';
import InputDOB from 'components/InputDOB';
import InputPhone from 'components/InputPhone';
import { ICustomer } from 'features/customer/services/types/api';
import styled from 'styled-components';
import TextAreaSuggestions from './TextAreaSuggestions';
import { CustomerDetailsForm } from 'features/customer/services/types/customer';
import moment from 'moment';
import { IResponseDataBody } from 'services/response';
import customerApis from 'features/customer/services/apis';
import toast from 'utils/toast';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { get } from 'lodash';
import Text from 'components/Text';
import { formatPhone } from 'utils/formatPhone';
type CreateOrUpdateCustomerFormProps = {
  data?: ICustomer;
  onClose: () => void;
  onSuccess?: (data?: ICustomer) => void;
};

const CreateOrUpdateCustomerForm = ({
  data,
  onClose,
  onSuccess,
}: CreateOrUpdateCustomerFormProps) => {
  const [form] = Form.useForm();
  const setLoadingPage = useSetLoadingPage();

  const onFinish = async (values: CustomerDetailsForm) => {
    const successText = data?.id ? 'Edit customer successfully!' : 'Create customer successfully!';
    const errorText = data?.id ? 'Edit customer failed!' : 'Create customer failed!';
    try {
      setLoadingPage(true);
      const body: CustomerDetailsForm = {
        customerId: data?.id || '',
        point: values?.point || 0,
        fullName: values?.fullName || '',
        dob: values?.dob ? moment(values.dob).format('MM-DD-YYYY') : '',
        phone: (values?.phone || '')?.replace(/[^0-9]/g, '') || '',
        mail: values?.mail || '',
        street: values?.street || '',
        city: values?.city || '',
        state: values?.state || '',
        zipCode: values?.zipCode || '',
        note: values?.note || '',
        hobbies: values?.hobbies || '',
        health: values?.health || '',
        color: values?.color || '',
      };
      const res: IResponseDataBody<ICustomer> = data?.id
        ? await customerApis.editCustomer(body)
        : await customerApis.createCustomer(body);
      if (res?.data?.data) {
        toast.success(successText);
        onClose();
        onSuccess?.(res.data.data);
      } else {
        toast.error(errorText);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || errorText);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    if (data) {
      const fieldsValue = {
        fullName: data.name || '',
        phone: data.phone ? formatPhone(data.phone) : '',
        dob: data.birthday ? moment(data.birthday).format('MM-DD-YYYY') : '',
        mail: data.email || '',
        street: data.street || '',
        city: data.city || '',
        state: data.state || '',
        zipCode: data.zipCode || '',
        note: data.note || '',
        hobbies: data.hobbies || '',
        health: data.health || '',
        color: data.color || '',
      };
      form.setFieldsValue(fieldsValue);
    }
  }, [data]);

  return (
    <FormContainerStyled>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <ContainerStyled>
          <RowStyled>
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  name='fullName'
                  label='Full Name'
                  rules={[{ required: true, message: 'Please input Full Name!' }]}
                >
                  <Input placeholder='Enter your Full Name' />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name='phone'
                  label='Phone'
                  rules={[
                    {
                      required: true,
                      message: 'Please input your phone number!',
                    },
                  ]}
                >
                  <InputPhone placeholder='Enter your phone number' />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='dob' label={'DOB'}>
                  <InputDOB />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='mail' label='Email' rules={[{ type: 'email', message: 'Please input a valid email!' }]}>
                  <Input placeholder='Enter your Email' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item name='street' label='Street'>
                  <Input placeholder='Enter your Street' />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='city' label='City'>
                  <Input placeholder='Enter your City' />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='state' label='State'>
                  <Input placeholder='Enter your State' />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='zipCode' label='Zip Code'>
                  <Input placeholder='Enter your Zip Code' />
                </Form.Item>
              </Col>
            </Row>
          </RowStyled>
          <RowStyled>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name='note' label='Note'>
                  <TextArea className='text-area' placeholder='Please enter...' rows={4} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name='hobbies' label='Hobbies'>
                  <TextArea className='text-area' placeholder='Please enter...' rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </RowStyled>
          <RowStyled>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name='health' label='Health Status:'>
                  <TextAreaSuggestions
                    suggestions={[
                      'Diabetes',
                      'Allergies',
                      'Corns',
                      'Hypersensitive',
                      'Swelling',
                      'Skin Irritation',
                      'Nail Infection',
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name='color' label='Nail polish:'>
                  <TextAreaSuggestions
                    suggestions={['Nude Tones', 'Pastel Pinks', 'Classic Red', 'Blue Color']}
                  />
                </Form.Item>
              </Col>
            </Row>
          </RowStyled>
        </ContainerStyled>

        <ButtonGroupStyled>
          <Button ntype='SECONDARY' onClick={onClose} block>
            <Text variant='H7'>Close</Text>
          </Button>

          <Button ntype='PRIMARY' block htmlType='submit'>
            <Text variant='H7' color='text_1'>
              OK
            </Text>
          </Button>
        </ButtonGroupStyled>
      </Form>
    </FormContainerStyled>
  );
};

export default CreateOrUpdateCustomerForm;

const ButtonGroupStyled = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 16px;
  background: #fff;
  padding: 16px 16px 24px 16px;
  box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.15);
`;

const ContainerStyled = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const RowStyled = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  background: #fff;
  padding: 8px 16px;
  gap: 24px;
  align-self: stretch;
  .text-area {
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const FormContainerStyled = styled.div`
  background: #f5f5f5;
  .ant-form-item {
    margin-bottom: 0;
  }
`;
